/**
 * Gabriel Safety Check
 * ────────────────────
 * Runs silently on cart updates to check for potential interactions between
 * cart items and the customer's saved health profile (medications, allergies,
 * conditions).
 *
 * - Only fires if the customer has a Gabriel Health Profile saved
 *   (via /pages/health-profile, stored in cookie or customer metafield)
 * - Completely invisible when nothing flags (99% of cases)
 * - Shows a small inline warning in the cart drawer only when a real flag exists
 * - Never blocks checkout — just informs
 *
 * INTEGRATION POINTS (stubbed for now, wire to real services later):
 * 1. loadProfile() — reads cookie; real impl should also hit customer metafield
 *    via app proxy for logged-in users
 * 2. GABRIEL_INTERACTION_DB — hardcoded interaction map; real impl should call
 *    Gabriel AI's /api/interactions endpoint with cart ingredients + profile
 * 3. routeFlagToClinician() — stub; real impl should POST to OpenLoop /
 *    CareValidate API with the flagged cart for clinician review
 */

(function() {
  'use strict';

  const COOKIE_NAME = 'apotheca_health_profile_draft';
  const DEBUG = false;

  function log(...args) { if (DEBUG) console.log('[GabrielSafety]', ...args); }
  function warn(...args) { console.warn('[GabrielSafety]', ...args); }

  // ─── Stubbed interaction database ────────────────────────────────────────
  // Real implementation should replace this with a live call to Gabriel AI's
  // interaction checker. This table covers the most common known-dangerous
  // supplement-medication interactions as a safety floor.
  const GABRIEL_INTERACTION_DB = {
    // medication keywords → supplement ingredient keywords that interact
    'warfarin':          ['vitamin k', 'ginkgo', 'garlic', 'fish oil', 'omega-3', 'vitamin e', 'coq10', 'st johns wort', 'st. john', 'green tea extract'],
    'coumadin':          ['vitamin k', 'ginkgo', 'garlic', 'fish oil', 'omega-3', 'vitamin e', 'coq10', 'st johns wort'],
    'eliquis':           ['ginkgo', 'garlic', 'fish oil', 'st johns wort', 'st. john'],
    'xarelto':           ['ginkgo', 'garlic', 'fish oil', 'st johns wort', 'st. john'],
    'metformin':         ['chromium', 'alpha-lipoic acid', 'berberine'],
    'levothyroxine':     ['calcium', 'iron', 'biotin', 'soy', 'ashwagandha'],
    'synthroid':         ['calcium', 'iron', 'biotin', 'soy', 'ashwagandha'],
    'lithium':           ['caffeine', 'psyllium'],
    'ssri':              ['st johns wort', 'st. john', '5-htp', 'sam-e', 'tryptophan'],
    'prozac':            ['st johns wort', 'st. john', '5-htp', 'sam-e', 'tryptophan'],
    'zoloft':            ['st johns wort', 'st. john', '5-htp', 'sam-e', 'tryptophan'],
    'maoi':              ['tyrosine', 'phenylalanine', '5-htp', 'sam-e'],
    'benzodiazepine':    ['kava', 'valerian', 'gaba', 'melatonin'],
    'xanax':             ['kava', 'valerian'],
    'statin':            ['red yeast rice', 'niacin', 'coq10'],
    'lipitor':           ['red yeast rice', 'niacin'],
    'blood pressure':    ['licorice', 'ephedra', 'yohimbe'],
    'ace inhibitor':     ['potassium'],
    'diuretic':          ['potassium', 'licorice'],
  };

  // Condition-based warnings (from profile.conditions[])
  const CONDITION_WARNINGS = {
    'pregnant':          ['vitamin a', 'retinol', 'black cohosh', 'blue cohosh', 'pennyroyal', 'licorice', 'senna', 'ephedra', 'goldenseal', 'high-dose vitamin c'],
    'breastfeeding':     ['fenugreek', 'sage', 'peppermint oil', 'parsley'],
    'blood_thinners':    ['vitamin k', 'ginkgo', 'garlic', 'fish oil', 'omega-3', 'vitamin e'],
    'immunosuppressed':  ['echinacea', 'astragalus', 'andrographis', 'elderberry'],
    'liver_disease':     ['kava', 'comfrey', 'chaparral', 'germander'],
    'kidney_disease':    ['potassium', 'magnesium', 'vitamin d high-dose', 'creatine'],
    'thyroid_disorder':  ['iodine', 'kelp', 'ashwagandha', 'tyrosine'],
    'seizures':          ['ephedra', 'evening primrose', 'gingko', 'ginseng'],
  };

  // ─── Profile loading ──────────────────────────────────────────────────────
  // Prefers the unified ApothecaProfile client (which merges Supabase non-PHI
  // with the cookie's PHI payload). Falls back to raw cookie read if the client
  // hasn't loaded yet (e.g. script ordering on a slow network).
  async function loadProfile() {
    if (window.ApothecaProfile && typeof window.ApothecaProfile.load === 'function') {
      try {
        const merged = await window.ApothecaProfile.load('safety_check');
        if (merged && merged._source !== 'empty') return merged;
        return null;
      } catch(e) {
        warn('ApothecaProfile.load failed, falling back to cookie:', e);
      }
    }
    // Fallback: raw cookie
    const match = document.cookie.match(new RegExp('(^|; )' + COOKIE_NAME + '=([^;]*)'));
    if (!match) return null;
    try {
      return JSON.parse(decodeURIComponent(match[2]));
    } catch(e) {
      return null;
    }
  }

  // ─── Interaction check ────────────────────────────────────────────────────
  /**
   * Check a single cart item against the health profile.
   * Returns: { flagged: boolean, warnings: [string, ...] }
   *
   * INTEGRATION POINT: Replace body with a call to Gabriel AI's interaction
   * checker for much higher accuracy. This implementation is a safety floor
   * based on a hardcoded known-interactions table.
   */
  function checkItem(item, profile) {
    const warnings = [];
    const itemText = ((item.title || '') + ' ' + (item.product_title || '') + ' ' + (item.vendor || '')).toLowerCase();

    // Check medications
    const meds = (profile.medications || []).map(m => m.toLowerCase());
    for (const med of meds) {
      for (const [medKey, supplementKeywords] of Object.entries(GABRIEL_INTERACTION_DB)) {
        if (med.includes(medKey)) {
          for (const supp of supplementKeywords) {
            if (itemText.includes(supp)) {
              warnings.push(`Contains "${supp}" which may interact with "${med}"`);
              break; // one warning per medication per item
            }
          }
        }
      }
    }

    // Check conditions
    const conditions = profile.conditions || [];
    for (const cond of conditions) {
      const unsafeIngredients = CONDITION_WARNINGS[cond] || [];
      for (const ing of unsafeIngredients) {
        if (itemText.includes(ing)) {
          warnings.push(`Contains "${ing}" — not recommended if ${cond.replace('_', ' ')}`);
          break;
        }
      }
    }

    // Check allergies (exact string match)
    const allergies = (profile.allergies || []).map(a => a.toLowerCase()).filter(a => a.length >= 3);
    for (const allergen of allergies) {
      if (itemText.includes(allergen)) {
        warnings.push(`Contains "${allergen}" (allergy flag)`);
      }
    }

    return { flagged: warnings.length > 0, warnings };
  }

  /**
   * Scan an entire cart object and return all flagged items.
   */
  function checkCart(cart, profile) {
    const flagged = [];
    const items = cart && cart.items ? cart.items : [];
    for (const item of items) {
      const result = checkItem(item, profile);
      if (result.flagged) {
        flagged.push({ item, warnings: result.warnings });
      }
    }
    return flagged;
  }

  // ─── UI: render warning into the cart drawer ─────────────────────────────
  function renderWarnings(flaggedItems) {
    // Look for an existing Apotheca cart drawer or cart page warning slot
    let slot = document.getElementById('gabriel-safety-warnings');
    if (!slot) {
      // Attempt to inject into common cart drawer locations
      const target = document.querySelector('.cart-drawer') ||
                     document.querySelector('#CartDrawer') ||
                     document.querySelector('.cart__items') ||
                     document.querySelector('.cart-page');
      if (!target) { log('no cart UI found, skipping render'); return; }
      slot = document.createElement('div');
      slot.id = 'gabriel-safety-warnings';
      slot.className = 'gabriel-safety-warnings';
      target.prepend(slot);
    }

    if (flaggedItems.length === 0) {
      slot.style.display = 'none';
      slot.innerHTML = '';
      return;
    }

    const html = `
      <div class="gsw-panel">
        <div class="gsw-header">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <div>
            <div class="gsw-title">Apotheca flagged ${flaggedItems.length} item${flaggedItems.length === 1 ? '' : 's'} in your cart</div>
            <div class="gsw-sub">Based on your saved profile. A clinician will review before shipping if you proceed. Powered by Gabriel AI.</div>
          </div>
        </div>
        <ul class="gsw-list">
          ${flaggedItems.map(f => `
            <li class="gsw-item">
              <div class="gsw-item-title">${escapeHtml(f.item.product_title || f.item.title || 'Item')}</div>
              <ul class="gsw-warning-list">
                ${f.warnings.map(w => `<li>${escapeHtml(w)}</li>`).join('')}
              </ul>
            </li>
          `).join('')}
        </ul>
        <div class="gsw-actions">
          <a href="/pages/health-profile" class="gsw-link">Update your profile</a>
          <button class="gsw-dismiss" onclick="document.getElementById('gabriel-safety-warnings').style.display='none';">Proceed anyway</button>
        </div>
      </div>
    `;
    slot.innerHTML = html;
    slot.style.display = 'block';
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  // ─── INTEGRATION POINT: clinician routing (stubbed) ─────────────────────
  function routeFlagToClinician(flaggedItems, cart) {
    // Real implementation: POST to OpenLoop or CareValidate API
    // Example:
    //   fetch('/apps/gabriel/clinician-review', {
    //     method: 'POST',
    //     body: JSON.stringify({ cart, flags: flaggedItems, profile: loadProfile() })
    //   });
    log('Would route to clinician:', flaggedItems);
  }

  // ─── Main: check cart on load + on every cart update ─────────────────────
  async function runCheck() {
    const profile = await loadProfile();
    if (!profile) { log('no profile, skipping'); return; }

    let cart;
    try {
      const resp = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      cart = await resp.json();
    } catch(e) {
      warn('failed to fetch cart:', e);
      return;
    }

    if (!cart || !cart.items || cart.items.length === 0) {
      renderWarnings([]);
      return;
    }

    const flagged = checkCart(cart, profile);
    log(`checked ${cart.items.length} items, ${flagged.length} flagged`);

    renderWarnings(flagged);

    // Fire integration point if anything flagged
    if (flagged.length > 0) {
      routeFlagToClinician(flagged, cart);
      // Tag the order at checkout so the fulfillment side can hold for review
      // (set a cookie the Shopify Checkout Extension can read)
      document.cookie = 'apotheca_cart_flagged=1; path=/; max-age=3600';
    } else {
      document.cookie = 'apotheca_cart_flagged=; path=/; max-age=0';
    }
  }

  // Intercept cart updates to re-run the check
  function hookCartUpdates() {
    // Shopify emits cart updates via multiple mechanisms depending on theme.
    // We listen for everything reasonable and de-dupe.
    const refresh = debounce(runCheck, 150);

    window.addEventListener('apotheca:health-profile-updated', refresh);
    window.addEventListener('cart:updated', refresh);
    document.addEventListener('cart:refresh', refresh);

    // Fetch intercept for /cart/add, /cart/change, /cart/update
    const origFetch = window.fetch;
    window.fetch = async function(url, opts) {
      const resp = await origFetch.apply(this, arguments);
      try {
        const u = (typeof url === 'string' ? url : url.url || '');
        if (/\/cart\/(add|change|update|clear)/.test(u)) {
          refresh();
        }
      } catch(e) {}
      return resp;
    };
  }

  function debounce(fn, ms) {
    let t;
    return function(...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  // Expose for manual testing
  window.GabrielSafety = {
    run: runCheck,
    loadProfile,
    checkCart,
    checkItem,
    // For QA: inject a fake profile without form
    _debugSetProfile: function(profile) {
      document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(profile))}; path=/; max-age=31536000`;
    },
  };

  // Init
  function init() {
    hookCartUpdates();
    runCheck();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
